import './styles.css';
import './utils/consoleFilter'; // Filtrar warnings de desarrollo
import { AuthManager } from './auth/AuthManager';
import { CalendarManager } from './calendar/CalendarManager';
import { UIManager } from './ui/UIManager';

/**
 * Clase principal de la aplicación Calendar Checker
 */
class App {
  private authManager: AuthManager;
  private calendarManager: CalendarManager;
  private uiManager: UIManager;

  constructor() {
    this.authManager = new AuthManager();
    this.calendarManager = new CalendarManager(this.authManager);
    this.uiManager = new UIManager();

    this.initialize();
  }

  /**
   * Inicializa la aplicación
   */
  private async initialize(): Promise<void> {
    try {
      // Mostrar información de diagnóstico
      this.authManager.diagnosticInfo();
      
      // Configurar los event listeners de la UI
      this.setupEventListeners();

      // Verificar si el usuario ya está autenticado
      const isAuthenticated = await this.authManager.checkAuthStatus();
      
      if (isAuthenticated) {
        await this.showCalendarScreen();
      } else {
        this.showLoginScreen();
      }
    } catch (error) {
      console.error('Error inicializando la aplicación:', error);
      this.uiManager.showError('Error al inicializar la aplicación');
    }
  }

  /**
   * Configura los event listeners de la aplicación
   */
  private setupEventListeners(): void {
    // Login button
    const loginButton = document.getElementById('login-button');
    loginButton?.addEventListener('click', () => this.handleLogin());

    // Logout button
    const logoutButton = document.getElementById('logout-button');
    logoutButton?.addEventListener('click', () => this.handleLogout());

    // Navigation buttons
    const prevWeekButton = document.getElementById('prev-week');
    const nextWeekButton = document.getElementById('next-week');
    const todayButton = document.getElementById('today-button');

    prevWeekButton?.addEventListener('click', () => this.handlePrevWeek());
    nextWeekButton?.addEventListener('click', () => this.handleNextWeek());
    todayButton?.addEventListener('click', () => this.handleToday());

    // Modal close
    const closeModal = document.getElementById('close-modal');
    const modalOverlay = document.getElementById('event-modal');
    
    closeModal?.addEventListener('click', () => this.uiManager.hideEventModal());
    modalOverlay?.addEventListener('click', (e) => {
      if (e.target === modalOverlay) {
        this.uiManager.hideEventModal();
      }
    });

    // Configurar callbacks para manejo de errores y diagnósticos
    this.uiManager.onRetryAuth = () => this.handleLogin();
    this.uiManager.onDiagnoseAuth = () => this.handleDiagnoseAuth();
    this.uiManager.onTroubleshootAuth = () => this.handleTroubleshootAuth();
    this.uiManager.onResetAuth = () => this.handleResetAuth();
  }

  /**
   * Maneja el proceso de login
   */
  private async handleLogin(): Promise<void> {
    try {
      this.uiManager.setLoginLoading(true);
      
      const success = await this.authManager.signIn();
      
      if (success) {
        await this.showCalendarScreen();
      }
    } catch (error: any) {
      console.error('Error en el login:', error);
      
      // Crear objeto de error estructurado para la nueva UI
      let appError = {
        code: 'UNKNOWN_ERROR',
        message: 'Error al iniciar sesión',
        details: error
      };
      
      if (error.code === 'USER_NOT_AUTHORIZED') {
        appError = error;
      } else if (error.code === 'GOOGLE_AUTH_ERROR') {
        appError = error;
      } else if (error.code === 'SIGNIN_FAILED') {
        appError.code = 'SIGNIN_FAILED';
        appError.message = 'Error de autenticación con Google. Verifica tu configuración.';
      } else if (error.error === 'popup_closed_by_user') {
        appError.code = 'USER_CANCELLED';
        appError.message = 'Proceso de login cancelado por el usuario.';
      } else if (error.error === 'access_denied') {
        appError.code = 'ACCESS_DENIED';
        appError.message = 'Acceso denegado. Es posible que la aplicación no esté verificada por Google o tu usuario no esté en la lista de prueba.';
      } else if (error.message && error.message.includes('Not a valid origin')) {
        appError.code = 'INVALID_ORIGIN';
        appError.message = 'Origen no válido. Verifica la configuración de dominios autorizados en Google Cloud Console.';
      } else if (error.details && error.details.error) {
        appError.code = 'GOOGLE_ERROR';
        appError.message = `Error de Google: ${error.details.error}`;
      }
      
      // Usar la nueva pantalla de error
      this.uiManager.showAuthError(appError);
    } finally {
      this.uiManager.setLoginLoading(false);
    }
  }

  /**
   * Maneja el proceso de logout
   */
  private async handleLogout(): Promise<void> {
    try {
      await this.authManager.signOut();
      this.showLoginScreen();
    } catch (error) {
      console.error('Error en el logout:', error);
      this.uiManager.showError('Error al cerrar sesión');
    }
  }

  /**
   * Navega a la semana anterior
   */
  private async handlePrevWeek(): Promise<void> {
    this.calendarManager.navigateToPreviousWeek();
    await this.loadCalendarEvents();
  }

  /**
   * Navega a la semana siguiente
   */
  private async handleNextWeek(): Promise<void> {
    this.calendarManager.navigateToNextWeek();
    await this.loadCalendarEvents();
  }

  /**
   * Navega a la semana actual
   */
  private async handleToday(): Promise<void> {
    this.calendarManager.navigateToCurrentWeek();
    await this.loadCalendarEvents();
  }

  /**
   * Muestra la pantalla de login
   */
  private showLoginScreen(): void {
    this.uiManager.showLoginScreen();
  }

  /**
   * Muestra la pantalla del calendario
   */
  private async showCalendarScreen(): Promise<void> {
    this.uiManager.showCalendarScreen();
    this.uiManager.initializeCalendarGrid();
    
    // Actualizar información del usuario en el header
    const currentUser = this.authManager.getCurrentUser();
    const ownerEmail = this.authManager.getOwnerEmail();
    
    if (currentUser) {
      this.uiManager.updateUserInfo(currentUser.name || currentUser.email, ownerEmail);
    }
    
    await this.loadCalendarEvents();
  }

  /**
   * Carga los eventos del calendario
   */
  private async loadCalendarEvents(): Promise<void> {
    try {
      this.uiManager.showLoadingState();
      
      // Obtener información del usuario actual
      const currentUser = this.authManager.getCurrentUser();
      const currentUserEmail = currentUser?.email || 'desconocido';
      
      // Intentar obtener calendarios del propietario primero
      const ownerCalendars = await this.calendarManager.getOwnerCalendars();
      
      // Si no hay calendarios del propietario disponibles, mostrar advertencia
      if (!ownerCalendars || ownerCalendars.length === 0) {
        console.warn('⚠️ No se encontraron calendarios del propietario para el usuario:', currentUserEmail);
        this.uiManager.showNoCalendarsState(currentUserEmail);
        return;
      }
      
      // Obtener eventos de la semana
      const events = await this.calendarManager.getWeekEvents();
      
      if (events && events.length > 0) {
        this.uiManager.displayEvents(events);
      } else {
        // Si hay calendarios pero no eventos, mostrar estado vacío normal
        this.uiManager.showEmptyState();
      }
      
      // Actualizar la información de la semana en el header
      const weekInfo = this.calendarManager.getCurrentWeekInfo();
      this.uiManager.updateWeekInfo(weekInfo);
      
    } catch (error) {
      console.error('Error cargando eventos del calendario:', error);
      this.uiManager.showError('Error al cargar los eventos del calendario');
    }
  }

  /**
   * Maneja el diagnóstico completo del sistema de autenticación
   */
  private handleDiagnoseAuth(): void {
    console.log('🔍 Ejecutando diagnóstico completo...');
    
    // Ejecutar diagnóstico detallado
    this.authManager.diagnosticInfo();
    
    // Mostrar información en la consola
    console.log('📋 Revisa la consola del navegador para ver el diagnóstico completo');
    console.log('💡 Busca líneas marcadas con ❌ para identificar problemas');
  }

  /**
   * Intenta resolver automáticamente problemas de OAuth
   */
  private async handleTroubleshootAuth(): Promise<void> {
    try {
      console.log('🔧 Intentando resolver problemas automáticamente...');
      
      const success = await this.authManager.troubleshootAndRetry();
      
      if (success) {
        console.log('✅ Problemas resueltos, reintentando login...');
        await this.handleLogin();
      } else {
        console.log('❌ No se pudieron resolver los problemas automáticamente');
        this.uiManager.showError('No se pudieron resolver los problemas automáticamente. Revisa el diagnóstico y configura manualmente.');
      }
    } catch (error: any) {
      console.error('Error en troubleshooting:', error);
      
      let message = 'Error durante la resolución automática: ';
      if (error.code) {
        message += error.message;
      } else {
        message += 'Error inesperado';
      }
      
      this.uiManager.showError(message);
    }
  }

  /**
   * Reinicia completamente el sistema de autenticación
   */
  private async handleResetAuth(): Promise<void> {
    try {
      console.log('🗑️ Reiniciando sistema de autenticación...');
      await this.authManager.resetAuthentication();
    } catch (error) {
      console.error('Error al reiniciar autenticación:', error);
      // Si el reset falla, al menos mostramos el login screen
      this.uiManager.showLoginScreen();
    }
  }
}

// Inicializar la aplicación cuando el DOM esté listo
document.addEventListener('DOMContentLoaded', () => {
  new App();
});
